import React, { useEffect } from "react";
import { Modal, Form, Select, Input, Radio, Descriptions, message } from "antd";
import { useErrorTopicIdOptions } from "../../../../hooks/useErrorTopic";
import { useSaveCaseAudit } from "../../../../hooks/useCaseAudit";

const { TextArea } = Input;

// ให้ admin ตรวจเคสเองจากหน้ารายงานเคสทั้งหมด
// หัวข้อดึงจาก master data ชุดเดียวกับ Manual DEP / Report (Panel)
const AuditCaseModal = ({ visible, onClose, record, onSaved }) => {
  const [form] = Form.useForm();
  const { options: topicOptions } = useErrorTopicIdOptions();
  const { mutateAsync: saveAudit, isLoading } = useSaveCaseAudit();

  // เปิดซ้ำอีกเคส ต้องไม่ค้างค่าของเคสก่อนหน้า
  // ถ้าเคยตรวจแล้ว ให้เติมผลเดิมเข้าไปเพื่อแก้ไขต่อได้
  useEffect(() => {
    if (!visible) return;
    form.setFieldsValue({
      result: record?.auditResult || "Pass",
      errorTopicId: record?.auditTopicId ?? undefined,
      note: record?.auditNote || "",
    });
  }, [visible, record, form]);

  const handleOk = async () => {
    try {
      const values = await form.validateFields();
      const res = await saveAudit({
        inputId: record.id,
        result: values.result,
        errorTopicId: values.result === "Fail" ? values.errorTopicId : null,
        note: values.note || "",
      });
      // ตรวจผ่านแล้ว backend จะเคลียร์รายการเดียวกันในหน้า Manual DEP ให้ด้วย
      // บอกผู้ใช้ให้รู้ ไม่งั้นจะไม่รู้ว่าไม่ต้องไปกดซ้ำอีกหน้า
      message.success(
        res?.result?.syncedManualDep > 0
          ? "บันทึกผลตรวจสำเร็จ — เคลียร์รายการในหน้า Manual DEP ให้แล้ว"
          : "บันทึกผลตรวจสำเร็จ"
      );
      onClose();
      if (onSaved) onSaved();
    } catch (error) {
      if (error?.errorFields) return; // validation ของ antd
      await message.destroy();
      message.error(error?.response?.data?.message || "บันทึกไม่สำเร็จ");
    }
  };

  if (!record) return null;

  return (
    <Modal
      title="ตรวจสอบเคส"
      open={visible}
      onOk={handleOk}
      onCancel={onClose}
      okText="บันทึกผลตรวจ"
      cancelText="ยกเลิก"
      okButtonProps={{ loading: isLoading, disabled: isLoading }}
      confirmLoading={isLoading}
      width={620}
    >
      <Descriptions bordered size="small" column={1} style={{ marginBottom: 16 }}>
        <Descriptions.Item label="User ID">{record.userId || "-"}</Descriptions.Item>
        <Descriptions.Item label="Case">
          {[record.caseName, record.case2Display, record.case3Display]
            .filter(Boolean)
            .join(" / ") || "-"}
        </Descriptions.Item>
        <Descriptions.Item label="แอดมิน (จัดการ)">
          {record.finish_by_name || "-"}
        </Descriptions.Item>
        <Descriptions.Item label="Amount">{record.amount ?? "-"}</Descriptions.Item>
      </Descriptions>

      {record.auditResult && (
        <div style={{ marginBottom: 12, color: "#fa8c16" }}>
          เคสนี้เคยตรวจแล้วเมื่อ {record.auditedDate || "-"}
          {record.audited_by_name ? ` โดย ${record.audited_by_name}` : ""} — บันทึกใหม่จะทับผลเดิม
        </div>
      )}

      <Form form={form} layout="vertical">
        <Form.Item name="result" label="ผลการตรวจ" rules={[{ required: true }]}>
          <Radio.Group>
            <Radio.Button value="Pass">ผ่าน</Radio.Button>
            <Radio.Button value="Fail">ผิด</Radio.Button>
          </Radio.Group>
        </Form.Item>

        <Form.Item
          noStyle
          shouldUpdate={(prev, cur) => prev.result !== cur.result}
        >
          {({ getFieldValue }) =>
            getFieldValue("result") === "Fail" ? (
              <Form.Item
                name="errorTopicId"
                label="หัวข้อที่ผิด"
                rules={[{ required: true, message: "กรุณาเลือกหัวข้อ" }]}
              >
                <Select
                  placeholder="เลือกหัวข้อความผิดพลาด"
                  options={topicOptions}
                  showSearch
                  optionFilterProp="label"
                />
              </Form.Item>
            ) : null
          }
        </Form.Item>

        <Form.Item name="note" label="รายละเอียดเพิ่มเติม">
          <TextArea rows={4} placeholder="อธิบายสิ่งที่พบ..." />
        </Form.Item>
      </Form>
    </Modal>
  );
};

export default AuditCaseModal;
